// Floating contextual toolbar for image and frame elements. Same positioning
// contract as TextFloatingToolbar: rAF loop reading the page-canvas rect,
// auto-flip below when there's no room above, hidden while transforming.
// All mutations dispatch through onUpdateElement(id, partial); the caller
// wraps it with a history snapshot so every action is one undo step.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  ImagePlus, Crop, FlipHorizontal2, FlipVertical2, RotateCw, ChevronDown, Check, Frame,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { DEFAULT_STROKE } from './svgUtils';
import { getSvgColorCapabilities, recolorAllPatch, setSvgColorPatch } from './svgColorEdits';
import ElementQuickActions from './ElementQuickActions';

const TOOLBAR_OFFSET = 10;
const APPROX_TOOLBAR_HEIGHT = 40;
const TOOLBAR_MIN_GAP = 8;

const FIT_MODES = [
  { id: 'cover', label: 'Cover' },
  { id: 'contain', label: 'Contain' },
  { id: 'fill', label: 'Fill' },
  { id: 'stretch', label: 'Stretch' },
];

const ROTATION_PRESETS = [0, 45, 90, 180, 270];

// Flip + rotation apply to every visual element; replace/crop/fit only to photo
// containers (image + frame). Exported for regression coverage.
export const TRANSFORM_TYPES = ['image', 'frame', 'svg', 'shape', 'sticker', 'penPath', 'drawing'];
export function getToolbarCapabilities(element) {
  const type = element?.type;
  return {
    applicable: TRANSFORM_TYPES.includes(type),
    showImageControls: type === 'image' || type === 'frame',
  };
}

const Btn = ({ active, title, onClick, children, disabled }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    aria-pressed={!!active}
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'h-7 px-1.5 min-w-7 flex items-center justify-center gap-1 rounded-md text-gray-600 text-xs',
      'hover:text-gray-900 hover:bg-gray-100 transition-colors',
      active && 'bg-violet-50 text-violet-700 ring-1 ring-violet-200',
      disabled && 'opacity-30 hover:bg-transparent cursor-not-allowed'
    )}
  >
    {children}
  </button>
);

const Sep = () => <div className="w-px h-5 bg-gray-200 mx-0.5" />;

// Compact fill/stroke color editing for SVG elements. Writes colorMap/fill/
// stroke through onUpdate (history-wrapped by the caller) so changes persist
// to save/load, thumbnails, and export via processSvgForRender.
const SvgColorControls = ({ element, onUpdate }) => {
  const { hasEditableFills, hasStroke, usesPalette, editableColors } = getSvgColorCapabilities(element);
  const stroke = { ...DEFAULT_STROKE, ...(element.stroke || {}) };
  const swatch = usesPalette
    ? (element.colorMap[editableColors[0]] || '#000000')
    : (element.fill || '#6366f1');

  return (
    <>
      {hasEditableFills && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              title="Colors"
              data-testid="svg-fill-trigger"
              className="h-7 px-1.5 flex items-center gap-1.5 rounded-md hover:bg-gray-100 transition-colors"
            >
              <span className="w-4 h-4 rounded border border-gray-300" style={{ background: swatch }} />
              <span className="text-xs text-gray-600">Colors</span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" side="bottom" sideOffset={6} className="w-56 p-2 space-y-2">
            <div className="space-y-1">
              <span className="text-[11px] font-medium text-gray-500">Recolor all</span>
              <input
                type="color"
                data-testid="svg-recolor-all"
                value={swatch.startsWith('#') ? swatch : '#000000'}
                onChange={(e) => onUpdate(recolorAllPatch(element, e.target.value))}
                className="w-full h-7 rounded border cursor-pointer p-0.5"
              />
            </div>
            {usesPalette && (
              <div className="space-y-1 pt-1 border-t border-gray-100">
                <span className="text-[11px] font-medium text-gray-500">Individual colors</span>
                <div className="grid grid-cols-4 gap-1.5">
                  {editableColors.map((orig) => (
                    <input
                      key={orig}
                      type="color"
                      title={orig}
                      data-testid={`svg-color-${orig}`}
                      value={(element.colorMap[orig] || '#000000').startsWith('#') ? element.colorMap[orig] : '#000000'}
                      onChange={(e) => onUpdate(setSvgColorPatch(element, orig, e.target.value))}
                      className="w-full h-7 rounded border cursor-pointer p-0.5"
                    />
                  ))}
                </div>
              </div>
            )}
          </PopoverContent>
        </Popover>
      )}
      {hasStroke && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              title="Stroke"
              data-testid="svg-stroke-trigger"
              className="h-7 px-1.5 flex items-center gap-1.5 rounded-md hover:bg-gray-100 transition-colors"
            >
              <span
                className="w-4 h-4 rounded-full border-2"
                style={{ borderColor: stroke.enabled ? stroke.color : '#d1d5db', background: 'transparent' }}
              />
              <span className="text-xs text-gray-600">Stroke</span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" side="bottom" sideOffset={6} className="w-52 p-2 space-y-2">
            <label className="flex items-center gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                data-testid="svg-stroke-enabled"
                checked={!!stroke.enabled}
                onChange={(e) => onUpdate({ stroke: { ...stroke, enabled: e.target.checked } })}
              />
              Enable stroke
            </label>
            <div className="space-y-1">
              <span className="text-[11px] font-medium text-gray-500">Color</span>
              <input
                type="color"
                data-testid="svg-stroke-color"
                value={(stroke.color || '#000000').startsWith('#') ? stroke.color : '#000000'}
                onChange={(e) => onUpdate({ stroke: { ...stroke, color: e.target.value, enabled: true } })}
                className="w-full h-7 rounded border cursor-pointer p-0.5"
              />
            </div>
            <div className="space-y-1">
              <span className="text-[11px] font-medium text-gray-500">Width: {stroke.width}px</span>
              <input
                type="range"
                min={0}
                max={20}
                step={0.5}
                value={stroke.width ?? 2}
                onChange={(e) => onUpdate({ stroke: { ...stroke, width: Number(e.target.value), enabled: true } })}
                className="w-full"
              />
            </div>
          </PopoverContent>
        </Popover>
      )}
    </>
  );
};

const ImageFloatingToolbar = ({
  element,
  pageCanvasId,
  zoom,
  onUpdateElement,
  isInteracting,
  onRequestReplace,
  onRequestCrop,
  onDuplicate,
  onDelete,
  onToggleLock,
  onLayerOrder,
  onConvertToFrame,
  onSaveAsFrame,
}) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  const [degreeDraft, setDegreeDraft] = useState('');

  const isFrame = element?.type === 'frame';
  const { applicable, showImageControls } = getToolbarCapabilities(element);
  // Shape / SVG / sticker can become a custom frame (Canva "Convert to Frame" /
  // "Use as Frame").
  const canConvert = element && ['shape', 'svg', 'sticker', 'penPath', 'drawing'].includes(element.type);

  useEffect(() => {
    if (!applicable) return;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const canvas = document.getElementById(pageCanvasId);
      if (!canvas) { setPos(null); return; }
      const rect = canvas.getBoundingClientRect();
      const elTop = rect.top + (element.y || 0) * zoom;
      const elLeft = rect.left + (element.x || 0) * zoom;
      const elWidth = (element.width || 0) * zoom;
      const elHeight = (element.height || 0) * zoom;
      const toolbarW = ref.current?.offsetWidth || 380;
      const desiredLeft = elLeft + elWidth / 2 - toolbarW / 2;
      const left = Math.max(TOOLBAR_MIN_GAP,
        Math.min(window.innerWidth - toolbarW - TOOLBAR_MIN_GAP, desiredLeft));
      const placeAbove = elTop - APPROX_TOOLBAR_HEIGHT - TOOLBAR_OFFSET >= TOOLBAR_MIN_GAP;
      const top = placeAbove
        ? elTop - APPROX_TOOLBAR_HEIGHT - TOOLBAR_OFFSET
        : elTop + elHeight + TOOLBAR_OFFSET;
      setPos((prev) => {
        if (prev && Math.abs(prev.left - left) < 0.5 && Math.abs(prev.top - top) < 0.5) return prev;
        return { left, top };
      });
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    return () => { cancelled = true; };
  }, [applicable, element?.id, element?.x, element?.y, element?.width, element?.height, pageCanvasId, zoom, element]);

  const setProp = useCallback((patch) => {
    if (!element) return;
    onUpdateElement(element.id, patch);
  }, [element, onUpdateElement]);

  if (!applicable || !pos || isInteracting) return null;

  const fit = element.fit || 'cover';
  const rotation = Math.round(element.rotation || 0);
  const hasImage = isFrame ? !!element.image : true;

  const commitDegree = () => {
    const n = Number(degreeDraft);
    if (degreeDraft !== '' && Number.isFinite(n)) setProp({ rotation: ((n % 360) + 360) % 360 });
    setDegreeDraft('');
  };

  return (
    <div
      ref={ref}
      role="toolbar"
      aria-label="Image tools"
      data-testid="image-floating-toolbar"
      style={{ position: 'fixed', left: pos.left, top: pos.top, zIndex: 60 }}
      className={cn(
        'flex items-center gap-0.5 bg-white border border-gray-200 rounded-xl shadow-lg',
        'px-1.5 py-1 text-sm select-none'
      )}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {showImageControls && (
        <>
          <Btn title="Replace image" onClick={onRequestReplace}>
            <ImagePlus className="w-4 h-4" />
            <span>Replace</span>
          </Btn>
          <Btn title="Crop" onClick={onRequestCrop} disabled={!hasImage}>
            <Crop className="w-4 h-4" />
            <span>Crop</span>
          </Btn>
          <Sep />
        </>
      )}
      {element.type === 'svg' && (
        <>
          <SvgColorControls element={element} onUpdate={setProp} />
          <Sep />
        </>
      )}
      {canConvert && onConvertToFrame && (
        <>
          <Btn title={element.type === 'svg' ? 'Use as Frame' : 'Convert to Frame'} onClick={onConvertToFrame}>
            <Frame className="w-4 h-4" />
            <span>Frame</span>
          </Btn>
          <Sep />
        </>
      )}
      {isFrame && onSaveAsFrame && (
        <>
          <Btn title="Save as Custom Frame" onClick={onSaveAsFrame}>
            <Frame className="w-4 h-4" />
            <span>Save</span>
          </Btn>
          <Sep />
        </>
      )}
      <Btn title="Flip horizontal" active={!!element.flipX} onClick={() => setProp({ flipX: !element.flipX })}>
        <FlipHorizontal2 className="w-4 h-4" />
      </Btn>
      <Btn title="Flip vertical" active={!!element.flipY} onClick={() => setProp({ flipY: !element.flipY })}>
        <FlipVertical2 className="w-4 h-4" />
      </Btn>
      <Sep />
      {/* Fit mode (how the photo fills the frame / image box) */}
      {showImageControls && (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            title="Fit mode"
            className="h-7 bg-gray-100 rounded-md text-xs px-2 flex items-center gap-1 hover:bg-gray-200 transition-colors"
          >
            <span className="capitalize">{fit}</span>
            <ChevronDown className="w-3 h-3 text-gray-500" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" side="bottom" sideOffset={6} className="w-32 p-1">
          {FIT_MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setProp({ fit: m.id })}
              className={cn(
                'w-full flex items-center justify-between px-2 py-1.5 rounded-md text-xs text-left hover:bg-gray-100',
                fit === m.id && 'bg-violet-50 text-violet-700'
              )}
            >
              {m.label}
              {fit === m.id && <Check className="w-3 h-3" />}
            </button>
          ))}
        </PopoverContent>
      </Popover>
      )}
      {showImageControls && <Sep />}
      {/* Rotation presets + custom degree */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            title="Rotate"
            className="h-7 bg-gray-100 rounded-md text-xs px-2 flex items-center gap-1 hover:bg-gray-200 transition-colors"
          >
            <RotateCw className="w-3.5 h-3.5" />
            <span>{rotation}°</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" side="bottom" sideOffset={6} className="w-44 p-2 space-y-1">
          <div className="grid grid-cols-5 gap-1">
            {ROTATION_PRESETS.map((deg) => (
              <button
                key={deg}
                type="button"
                onClick={() => setProp({ rotation: deg })}
                className={cn(
                  'h-7 rounded-md text-[11px] hover:bg-gray-100',
                  rotation === deg && 'bg-violet-50 text-violet-700 ring-1 ring-violet-200'
                )}
              >
                {deg}°
              </button>
            ))}
          </div>
          <input
            type="number"
            placeholder="Custom °"
            value={degreeDraft}
            onChange={(e) => setDegreeDraft(e.target.value)}
            onBlur={commitDegree}
            onKeyDown={(e) => { if (e.key === 'Enter') commitDegree(); }}
            className="w-full h-7 px-2 text-xs border border-gray-200 rounded-md"
          />
        </PopoverContent>
      </Popover>
      <Sep />
      <ElementQuickActions
        element={element}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onToggleLock={onToggleLock}
        onLayerOrder={onLayerOrder}
      />
    </div>
  );
};

export default ImageFloatingToolbar;
